export type PlaceholderSfxEvent =
  | 'throw'
  | 'bounce'
  | 'merge-small'
  | 'merge-medium'
  | 'merge-large'
  | 'game-over'
  | 'new-best'
  | 'button-tap';

export interface PlaceholderSfxHandle {
  play(event: PlaceholderSfxEvent): void;
  destroy(): void;
}

export interface PlaceholderSfxOptions {
  ctx?: AudioContext;
  /** Volume in [0,1]. Default 0.25 so placeholders are quieter than real SFX. */
  volume?: number;
}

export function createPlaceholderSfx(opts: PlaceholderSfxOptions = {}): PlaceholderSfxHandle {
  const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
  if (!opts.ctx && !Ctor) throw new Error('[placeholderSfx] No AudioContext available');
  const ctx = opts.ctx ?? new (Ctor as typeof AudioContext)();
  const vol = opts.volume ?? 0.25;

  function tone(freq: number, durMs: number, kind: OscillatorType = 'sine', startGain = vol) {
    if (ctx.state === 'closed') return;
    if (ctx.state === 'suspended') void ctx.resume?.();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = kind;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(startGain, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durMs / 1000);
  }

  function chord(freqs: number[], durMs: number, kind: OscillatorType = 'triangle') {
    for (const f of freqs) tone(f, durMs, kind, vol / freqs.length);
  }

  return {
    play(event) {
      switch (event) {
        case 'throw':
          tone(880, 200, 'sine');
          tone(440, 200, 'triangle', vol / 2);
          break;
        case 'bounce':
          tone(220, 100, 'square');
          break;
        case 'merge-small':
          chord([880, 1320], 250, 'triangle');
          break;
        case 'merge-medium':
          chord([660, 990, 1320], 400, 'triangle');
          break;
        case 'merge-large':
          chord([440, 660, 880, 1100], 600, 'triangle');
          break;
        case 'game-over':
          tone(440, 400, 'sine');
          tone(330, 400, 'sine');
          break;
        case 'new-best':
          tone(660, 200, 'sine');
          tone(880, 200, 'sine');
          tone(1100, 300, 'sine');
          break;
        case 'button-tap':
          tone(800, 80, 'square');
          break;
      }
    },
    destroy() {
      // Don't close shared ctx; just stop creating new nodes.
    },
  };
}
